import React, {
  useRef, useState, useEffect, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactFlow, {
  Background,
  Controls,
  ReactFlowProvider,
  addEdge,
} from 'react-flow-renderer';
import { DateTime } from 'luxon';
import { getIdMap } from 'ontraccr-common/lib/Common';
import { getContactAddressBooks } from 'ontraccr-common/lib/Contacts';

import WorkflowAddSlider from './WorkflowAddSlider';
import WorkflowTriggerNode from './WorkflowTriggerNode';
import WorkflowApproverNode from './WorkflowApproverNode';
import WorkflowEditNode from './WorkflowEditNode';
import WorkflowEmailNode from './WorkflowEmailNode';
import WorkflowPushNode from './WorkflowPushNode';
import WorkflowPDFNode from './WorkflowPDFNode';
import WorkflowUserNode from './WorkflowUserNode';
import WorkflowCompleteNode from './WorkflowCompleteNode';
import WorkflowFormTriggerNode from './WorkflowFormTriggerNode';
import WorkflowDispatchNode from './WorkflowDispatchNode';
import WorkflowStatusNode from './WorkflowStatusNode';
import WorkflowTimeDelayNode from './WorkflowTimeDelayNode';
import WorkflowQuickBooksInvoice from './WorkflowQuickBooksSync';
import WorkflowPaymentNode from './WorkflowPaymentNode';
import WorkflowUpdateContractNode from './WorkflowUpdateContractNode';
import WorkflowCreateContractNode from './WorkflowCreateContractNode';
import WorkflowMaterialDebitStep from './WorkflowMaterialDebitStep';
import WorkflowLogicalYesNoNode from './WorkflowLogicalYesNoNode';
import WorkflowCreateTimeNode from './WorkflowCreateTimeNode';
import WorkflowCreateProfileNode from './WorkflowCreateProfileNode';
import WorkflowLogicalLoopNode from './WorkflowLogicalLoopNode';
import WorkflowFormUpdateNode from './WorkflowFormUpdateNode';
import WorkflowCreateBoardCardNode from './WorkflowCreateBoardCardNode';
import WorkflowCardUpdateNode from './WorkflowCardUpdateNode';
import WorkflowUpdateCostNode from './WorkflowUpdateCostNode';
import WorkflowUpdateEquipmentNode from './WorkflowUpdateEquipmentNode';
import WorkflowCreatePayableInvoiceNode from './WorkflowCreatePayableInvoiceNode';
import WorkflowExternalSignatureNode from './WorkflowExternalSignatureNode';
import WorkflowLogicalConditionNode from './WorkflowLogicalConditionNode';
import WorkflowCreateTaskNode from './WorkflowCreateTaskNode';
import WorkflowEclipseSync from './WorkflowEclipseSync';
import WorkflowCustomCobraPMANode from './WorkflowCustomCobraPMANode';
import WorkflowCreateCertificationNode from './WorkflowCreateCertificationNode';
import WorkflowCreateUserNode from './WorkflowCreateUserNode';
import WorkflowSageIntacctPONode from './WorkflowSageIntacctPONode';

import { DEFAULT_REQUIRED_SUBMIT, DEFAULT_TRIGGER_DAYS } from './formTrigger.constants';

import WorkflowEdge from './WorkflowEdge';
import FormWorkflowContext from './FormWorkflowContext';

import {
  FORMS_ADD_WORKFLOW_TYPE,
  FORMS_WORKFLOW_STEP_PREFIX,
  FORMS_WORKFLOW_STEP_1,
} from '../../nux/nux.constants';

import {
  createNuxEntry,
} from '../../nux/state/nux.actions';

import NuxFocusBackground from '../../nux/NuxFocusBackground';

import colors from '../../constants/Colors';
import {
  resolveFormDivisionIds,
  getInvalidMultiDivisionWorkflowNodes,
  getProjectIdsForDivisions,
} from '../formHelpers';
import useEntryCostcodes from '../hooks/useEntryCostcodes';

const getId = () => `ot_${Math.floor(new Date())}`;

const defaultElements = [
  {
    id: 'trigger',
    type: 'trigger',
    position: { x: 250, y: 25 },
  },
  {
    id: 'user',
    type: 'user',
    position: { x: 250, y: 350 },
  },
  {
    id: 'complete',
    type: 'complete',
    position: { x: 300, y: 550 },
    data: { label: 'User Completes Form' },
  },
  {
    id: 'trigger-user',
    source: 'trigger',
    target: 'user',
    arrowHeadType: 'arrow',
    type: 'smoothstep',
    style: { strokeWidth: 2 },
  },
  {
    id: 'user-complete',
    source: 'user',
    target: 'complete',
    arrowHeadType: 'arrow',
    type: 'smoothstep',
    style: { strokeWidth: 2 },
  },
];

const elementsToWorkflow = (elements = [], dataMap = {}) => {
  const nodes = [];
  const edges = [];
  elements.forEach((element) => {
    const {
      id,
      type,
      source,
      sourceHandle,
      target,
      targetHandle,
      position,
    } = element;
    if (type === 'customEdge' || type === 'smoothstep') {
      // Edge
      const newEdge = {
        source,
        target,
      };
      if (sourceHandle) newEdge.sourceHandle = sourceHandle;
      if (targetHandle) newEdge.targetHandle = targetHandle;
      edges.push(newEdge);
    } else if (type !== 'trigger' && type !== 'complete') {
      nodes.push({
        id,
        type,
        position,
        data: dataMap[id],
      });
    }
  });
  return { nodes, edges };
};

const workflowToElements = ({
  nodes = [],
  edges = [],
  triggers = [],
  isExternalForm,
}) => {
  const newDataMap = {};
  let elements = [defaultElements[0], defaultElements[2]];

  elements = elements.concat(
    edges.map(({
      source, sourceHandle, target, targetHandle,
    }) => {
      const sourceId = sourceHandle ?? source;
      const targetId = targetHandle ?? target;
      return {
        id: `${sourceId}-${targetId}`,
        source,
        sourceHandle,
        target,
        targetHandle,
        arrowHeadType: 'arrow',
        type: source === 'trigger' || source === 'user' ? 'smoothstep' : 'customEdge',
        style: { strokeWidth: 2 },
      };
    }),
  );

  nodes.forEach((node) => {
    const { id, data } = node;
    let nodeData = data;

    // Update user node to remove external user if not external form
    if (id === 'user') {
      if (!isExternalForm && nodeData?.users?.includes?.('external')) {
        nodeData = {
          ...nodeData,
          users: [],
        };
      } else if (isExternalForm) {
        // Add external user if external form
        nodeData = {
          ...nodeData,
          users: ['external'],
        };
      }
    }

    const formattedNode = {
      ...node,
      data: nodeData,
    };
    newDataMap[id] = nodeData;
    elements.push(formattedNode);
  });

  let newTriggers = triggers;

  // Fix triggers if external form
  const hasExternalTrigger = triggers.includes('external');
  if (isExternalForm && !hasExternalTrigger) {
    newTriggers = ['external'];
  } else if (!isExternalForm && hasExternalTrigger) {
    newTriggers = ['manual'];
  }

  return {
    triggers: newTriggers,
    elements,
    newDataMap,
  };
};

const onDragOver = (event) => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
};

export default function FormWorkflows({
  name,
  templateId,
  formRef,
  isDisplay,
  isAdd,
  collected,
  sections,
  exportLocation,
  onWorkflowChange,
  currentStep,
  divisionId,
  divisionIds: propDivisionIds,
  isMultiDivisionForm = false,
  workflow = {},
  drawOptions = [],
  fileMap = {},
  currentFormData = {},
  projectId,
  useStandardTemplate,
  isExternalForm,
  libraryTemplateStatusesToAdd,
  setDisableNext,
  isV3Form,
}) {
  const {
    typeId,
  } = currentFormData;
  const dispatch = useDispatch();
  const reactFlowWrapper = useRef(null);

  const divisionIds = resolveFormDivisionIds({ divisionIds: propDivisionIds, divisionId });
  const effectiveDivisionId = divisionIds[0] ?? divisionId;

  const defaultTrigger = isExternalForm ? 'external' : 'manual';

  const nux = useSelector((state) => state.nux.nux);
  const activeNuxAction = useSelector((state) => state.nux.activeNuxAction);
  const projects = useSelector((state) => state.projects.projects);
  const phases = useSelector((state) => state.costcodes.phases);
  const customers = useSelector((state) => state.customers.customers);
  const users = useSelector((state) => state.users.users);
  const vendors = useSelector((state) => state.vendors.vendors);
  const equipment = useSelector((state) => state.equipment.equipment);
  const formTemplates = useSelector((state) => state.forms.templates);
  const labels = useSelector((state) => state.labels);
  const globalAddressBooks = useSelector((state) => state.contacts.globalAddressBooks);
  const buckets = useSelector((state) => state.buckets.buckets);

  const costcodeProjectIds = useMemo(
    () => getProjectIdsForDivisions(projects, divisionIds),
    [projects, divisionIds],
  );
  const { costcodes, costcodesLoading } = useEntryCostcodes({
    projectIds: costcodeProjectIds,
    divisionIds,
  });

  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [elements, setElements] = useState(defaultElements);
  const [triggers, setTriggers] = useState([defaultTrigger]);
  const [triggerDays, setTriggerDays] = useState(DEFAULT_TRIGGER_DAYS);
  const [triggerRequiredSubmit, setTriggerRequiredSubmit] = useState(DEFAULT_REQUIRED_SUBMIT);
  const [isSharedForm, setIsSharedForm] = useState(false);
  const [dataMap, setDataMap] = useState({});
  // We can't reset node configurations after initial render of workflow graph
  // So we need to update the key to force a reset with new divisionId or new sections
  const [workflowKey, setWorkflowKey] = useState();

  const invalidWorkflowNodeIds = useMemo(() => {
    if (!isMultiDivisionForm) return new Set();
    const workflowNodes = elements.filter((element) => element.type && !element.source);
    return new Set(
      getInvalidMultiDivisionWorkflowNodes({ nodes: workflowNodes }).map((node) => node.id),
    );
  }, [elements, isMultiDivisionForm]);

  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);
  const customerList = useMemo(() => Object.values(customers), [customers]);
  const vendorList = useMemo(() => Object.values(vendors), [vendors]);
  const formTemplateList = useMemo(() => Object.values(formTemplates), [formTemplates]);
  const contactAddressBooks = useMemo(() => (
    getContactAddressBooks(globalAddressBooks)
  ), [globalAddressBooks]);

  const onConnect = (params) => setElements((els) => addEdge({
    ...params,
    type: 'customEdge',
    arrowHeadType: 'arrow',
    id: getId(),
    style: {
      strokeWidth: 2,
    },
  }, els));
  const onLoad = (_reactFlowInstance) => setReactFlowInstance(_reactFlowInstance);

  const DEFAULT_EMAIL = 'forms@ontraccr.com';
  const DEFAULT_BODY = 'Completed form attached.\n\nSent by Ontraccr.';
  const DEFAULT_SUBJECT = `${name} form submitted`;

  const defaultDataMap = {
    email: {
      sender: DEFAULT_EMAIL,
      subject: DEFAULT_SUBJECT,
      text: DEFAULT_BODY,
      groupRecipients: true,
    },
    push: { message: '', messageField: undefined },
    dispatch: { duplicateWorkflow: true },
    timeDelay: {
      delayAnchor: 'workflowReached',
      delayOffset: { amount: 1, time: 'days' },
      delayDateFieldId: null,
    },
  };

  const onDrop = (event) => {
    event.preventDefault();
    const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
    const type = event.dataTransfer.getData('application/reactflow');
    const clientX = event.dataTransfer.getData('application/reactflow/clientX');
    const clientY = event.dataTransfer.getData('application/reactflow/clientY');
    const position = reactFlowInstance.project({
      x: event.clientX - reactFlowBounds.left - clientX,
      y: event.clientY - reactFlowBounds.top - clientY,
    });

    const defaultData = defaultDataMap[type];
    const id = getId();
    const newNode = {
      id,
      type,
      position,
      data: { label: `${type} node`, ...defaultData },
    };
    if (defaultData) {
      setDataMap((dataMap) => ({
        ...dataMap,
        [id]: defaultData,
      }));
    }

    setElements(elements.concat(newNode));
  };

  const edgeTypes = {
    customEdge: WorkflowEdge({ setElements, isDisplay }),
  };

  const nodeTypes = {
    complete: WorkflowCompleteNode({ isExternalForm }),
    user: WorkflowUserNode({
      setDataMap,
      isDisplay,
      divisionId: effectiveDivisionId,
      divisionIds,
      isExternalForm,
      isSharedForm,
      setIsSharedForm,
    }),
    trigger: WorkflowTriggerNode({
      setTriggers,
      setTriggerDays,
      setTriggerRequiredSubmit,
      isDisplay,
      workflow,
      divisionId: effectiveDivisionId,
      isExternalForm,
    }),
    approval: WorkflowApproverNode({
      setElements,
      setDataMap,
      isDisplay,
      divisionId: effectiveDivisionId,
      divisionIds,
      sections,
      isExternalForm,
      setDisableNext,
    }),
    dispatch: WorkflowDispatchNode({
      setElements,
      setDataMap,
      isDisplay,
      sections,
      name,
      setDisableNext,
    }),
    email: WorkflowEmailNode({
      setElements,
      setDataMap,
      isDisplay,
      divisionId: effectiveDivisionId,
      divisionIds,
      sections,
      name,
      isExternalForm,
      setDisableNext,
    }),
    createTime: WorkflowCreateTimeNode({
      setElements,
      setDataMap,
      isDisplay,
      sections,
      name,
      divisionId: effectiveDivisionId,
      divisionIds,
      setDisableNext,
      isExternalForm,
    }),
    status: WorkflowStatusNode({
      setElements,
      setDataMap,
      isDisplay,
      divisionId: effectiveDivisionId,
      templateStatuses: libraryTemplateStatusesToAdd,
      setDisableNext,
    }),
    timeDelay: WorkflowTimeDelayNode({
      setElements,
      setDataMap,
      isDisplay,
      sections,
      setDisableNext,
    }),
    push: WorkflowPushNode({
      setElements,
      setDataMap,
      isDisplay,
      divisionId: effectiveDivisionId,
      divisionIds,
      sections,
      isExternalForm,
      setDisableNext,
    }),
    updateContract: WorkflowUpdateContractNode({
      setElements,
      setDataMap,
      isDisplay,
      sections,
      name,
      setDisableNext,
    }),
    createContract: WorkflowCreateContractNode({
      setElements,
      setDataMap,
      isDisplay,
      sections,
      name,
      divisionId: effectiveDivisionId,
      setDisableNext,
    }),
    materialDebit: WorkflowMaterialDebitStep({
      setElements,
      setDataMap,
      isDisplay,
      sections,
      name,
      setDisableNext,
    }),
    pdf: WorkflowPDFNode({
      onNodeUpdate: setElements,
      collected,
      sections,
      formRef,
      exportLocation,
      setDataMap,
      isDisplay,
      drawOptions,
      fileMap,
      useStandardTemplate,
      setDisableNext,
      divisionId: effectiveDivisionId,
      divisionIds,
      isExternalForm,
    }),
    formTrigger: WorkflowFormTriggerNode({
      setElements,
      setDataMap,
      isDisplay,
      divisionId: effectiveDivisionId,
      divisionIds,
      templateId,
      sections,
      name,
      projectId,
      isExternalForm,
      setDisableNext,
    }),
    createBoardCard: WorkflowCreateBoardCardNode({
      setElements,
      setDataMap,
      name,
      sections,
      divisionId: effectiveDivisionId,
      divisionIds,
      isDisplay,
      isExternalForm,
      setDisableNext,
    }),
    createProfile: WorkflowCreateProfileNode({
      setElements,
      setDataMap,
      name,
      sections,
      divisionId: effectiveDivisionId,
      isDisplay,
      setDisableNext,
    }),
    edit: WorkflowEditNode({
      setElements, setDataMap, isDisplay, divisionId: effectiveDivisionId, divisionIds, sections, isExternalForm, isMultiDivisionForm, setDisableNext,
    }),
    qbInvoice: WorkflowQuickBooksInvoice({
      setElements,
      setDataMap,
      isDisplay,
      divisionId: effectiveDivisionId,
      templateId,
      sections,
      name,
      formTypeName: 'Invoice',
      setDisableNext,
    }),
    qbPO: WorkflowQuickBooksInvoice({
      setElements,
      setDataMap,
      isDisplay,
      divisionId: effectiveDivisionId,
      templateId,
      sections,
      name,
      formTypeName: 'PO',
    }),
    stripePayment: WorkflowPaymentNode({
      setElements,
      setDataMap,
      isDisplay,
      templateId,
      sections,
      name,
      isInvoice: true,
      setDisableNext,
    }),
    logicalYesNo: WorkflowLogicalYesNoNode({
      setElements,
      setDataMap,
      isDisplay,
      templateId,
      sections,
      name,
      setDisableNext,
    }),
    logicalLoop: WorkflowLogicalLoopNode({
      setElements,
      setDataMap,
      isDisplay,
      templateId,
      sections,
      name,
      setDisableNext,
    }),
    logicalCondition: WorkflowLogicalConditionNode({
      setElements,
      setDataMap,
      isDisplay,
      sections,
      customers: customerList,
      projects,
      users,
      costcodes,
      phases,
      projectIdMap,
      vendors: vendorList,
      equipment,
      formTemplates: formTemplateList,
      labels,
      contactAddressBooks,
      buckets,
      setDisableNext,
    }),
    formUpdate: WorkflowFormUpdateNode({
      setElements,
      setDataMap,
      isDisplay,
      divisionId: effectiveDivisionId,
      divisionIds,
      templateId,
      sections,
      name,
      setDisableNext,
    }),
    updateBoardCard: WorkflowCardUpdateNode({
      setElements,
      setDataMap,
      isDisplay,
      divisionId: effectiveDivisionId,
      divisionIds,
      templateId,
      sections,
      name,
      isExternalForm,
      setDisableNext,
    }),
    updateCost: WorkflowUpdateCostNode({
      isDisplay,
      setElements,
      setDataMap,
      sections,
      name,
      setDisableNext,
    }),
    createPayableInvoice: WorkflowCreatePayableInvoiceNode({
      isDisplay,
      setElements,
      setDataMap,
      sections,
      name,
      setDisableNext,
    }),
    externalSignature: WorkflowExternalSignatureNode({
      isDisplay,
      setDataMap,
      setElements,
      sections,
      divisionId: effectiveDivisionId,
      divisionIds,
      isExternalForm,
      setDisableNext,
      isV3Form,
    }),
    createSubtask: WorkflowCreateTaskNode({
      setElements,
      setDataMap,
      sections,
      isDisplay,
      setDisableNext,
    }),
    eclipse: WorkflowEclipseSync({
      setElements,
      setDataMap,
      isDisplay,
      divisionId: effectiveDivisionId,
      templateId,
      sections,
      name,
    }),
    updateEquipment: WorkflowUpdateEquipmentNode({
      setElements,
      setDataMap,
      isDisplay,
      sections,
      divisionId: effectiveDivisionId,
      divisionIds,
    }),
    customCobraPMA: WorkflowCustomCobraPMANode({
      setElements,
      setDataMap,
      isDisplay,
      sections,
      templateId,
      divisionId: effectiveDivisionId,
      divisionIds,
    }),
    createCertification: WorkflowCreateCertificationNode({
      setElements,
      setDataMap,
      isDisplay,
      sections,
      name,
      setDisableNext,
    }),
    createUser: WorkflowCreateUserNode({
      setElements,
      setDataMap,
      isDisplay,
      sections,
      name,
      setDisableNext,
    }),
    sageIntacctPO: WorkflowSageIntacctPONode({
      setElements,
      setDataMap,
      isDisplay,
      sections,
      name,
      divisionId: effectiveDivisionId,
      divisionIds,
      setDisableNext,
    }),
  };

  useEffect(() => {
    setWorkflowKey(`${name}-${effectiveDivisionId}-${isExternalForm}-${DateTime.local().toMillis()}`);
  }, [sections, effectiveDivisionId, name, isExternalForm]);

  useEffect(() => {
    const { elements: flowElements = [] } = reactFlowInstance ? reactFlowInstance.toObject() : {};
    const newWorkflow = {
      ...elementsToWorkflow(flowElements, dataMap),
      triggers,
      triggerDays,
      triggerRequiredSubmit,
      isSharedForm,
    };

    // Clearing out any disabled nodes that have been removed from workflow
    setDisableNext((prevDisableNext) => {
      const newObj = { ...prevDisableNext };
      const { nodes = [] } = newWorkflow;
      const nodeMap = getIdMap(nodes);

      Object.keys(newObj).forEach((nodeId) => {
        if (!(nodeId in nodeMap)) {
          newObj[nodeId] = false;
        }
      });

      return newObj;
    });

    onWorkflowChange(newWorkflow);
  }, [
    elements, // DO NOT REMOVE: not used in effect but seems to break saving the workflow
    reactFlowInstance,
    onWorkflowChange,
    triggers,
    triggerDays,
    dataMap,
    triggerRequiredSubmit,
    isSharedForm,
  ]);

  useEffect(() => {
    if (workflow?.triggers && workflow?.nodes && workflow?.edges) {
      const {
        triggers: initialTriggers,
        elements: initialElements,
        newDataMap,
      } = workflowToElements({ ...workflow, isExternalForm });
      setElements(initialElements);
      setTriggers(initialTriggers);
      setTriggerDays(workflow.triggerDays ?? DEFAULT_TRIGGER_DAYS);
      setIsSharedForm(workflow.isSharedForm ?? false);
      setTriggerRequiredSubmit(workflow.triggerRequiredSubmit ?? DEFAULT_REQUIRED_SUBMIT);
      setDataMap(newDataMap);
      if (reactFlowInstance) reactFlowInstance.fitView({ padding: 0.30 });
    }
  }, [workflow, isDisplay, reactFlowInstance, isExternalForm]);

  useEffect(() => {
    if (
      currentStep === 2
      && isAdd
      && dispatch
      && !nux.has(FORMS_ADD_WORKFLOW_TYPE)
      && reactFlowInstance
    ) {
      dispatch(createNuxEntry(FORMS_ADD_WORKFLOW_TYPE));
      // dispatch(startNuxAction(FORMS_WORKFLOW_STEP_1));
      reactFlowInstance.fitView({ padding: 0 });
    }
  }, [nux, currentStep, dispatch, isAdd, reactFlowInstance]);

  const contextValue = useMemo(() => ({
    elements,
    dataMap,
    costcodes,
    costcodesLoading,
    phases,
    projectIdMap,
    invalidWorkflowNodeIds,
  }), [
    elements,
    dataMap,
    costcodes,
    costcodesLoading,
    phases,
    projectIdMap,
    invalidWorkflowNodeIds,
  ]);

  const showNux = activeNuxAction && activeNuxAction.startsWith(FORMS_WORKFLOW_STEP_PREFIX);
  const isNuxStep1 = activeNuxAction === FORMS_WORKFLOW_STEP_1;

  return (
    <>
      <div
        className={
        isDisplay
          ? 'form-content-container'
          : 'form-content-container-editable'
      }
        style={{
          left: 0,
          right: 0,
          borderTop: isDisplay ? 'none' : `1px ${colors.DIVIDER_COLOR} solid`,
        }}
        ref={reactFlowWrapper}
      >
        <FormWorkflowContext.Provider value={contextValue}>
          <ReactFlowProvider>
            <ReactFlow
              key={workflowKey}
              style={{
                zIndex: showNux && !isNuxStep1 ? 1000 : 3,
              }}
              elements={elements}
              snapToGrid
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onLoad={onLoad}
              onConnect={onConnect}
              connectionLineType="smoothstep"
              connectionLineStyle={{
                strokeWidth: 2,
              }}
              minZoom={0.2}
              nodesConnectable={!isDisplay && !showNux}
              nodesDraggable={!isDisplay && !showNux}
              elementsSelectable={!isDisplay && !showNux}
            >
              {!showNux && <Background variant="lines" />}
              {!showNux && <Controls showInteractive={false} />}
            </ReactFlow>
          </ReactFlowProvider>
        </FormWorkflowContext.Provider>
        {!isDisplay && (
          <WorkflowAddSlider
            sections={sections}
            typeId={typeId}
            divisionId={effectiveDivisionId}
            divisionIds={divisionIds}
            isMultiDivisionForm={isMultiDivisionForm}
          />
        )}
      </div>
      <NuxFocusBackground show={showNux} />
    </>
  );
}
